// Guard: localStorage is not available in SSR or service-worker contexts.
const canUseStorage = () =>
  typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';

/**
 * Saves a value to persistent storage.
 * @param {string} key
 * @param {string} value
 */
export const saveItem = async (key, value) => {
  if (!canUseStorage()) return;
  try {
    localStorage.setItem(key, value);
  } catch (error) {
    console.error('Storage saveItem error:', error);
  }
};

/**
 * Retrieves a value from persistent storage.
 * @param {string} key
 * @returns {string|null}
 */
export const getItem = async (key) => {
  if (!canUseStorage()) return null;
  try {
    return localStorage.getItem(key);
  } catch (error) {
    console.error('Storage getItem error:', error);
    return null;
  }
};

/**
 * Synchronously retrieves a value from persistent storage.
 * Safe to use in lazy useState initializers and other sync contexts.
 * @param {string} key
 * @returns {string|null}
 */
export const getItemSync = (key) => {
  if (!canUseStorage()) return null;
  try {
    return localStorage.getItem(key);
  } catch (error) {
    console.error('Storage getItemSync error:', error);
    return null;
  }
};

/**
 * Removes a value from persistent storage.
 * @param {string} key
 */
export const removeItem = async (key) => {
  if (!canUseStorage()) return;
  try {
    localStorage.removeItem(key);
  } catch (error) {
    console.error('Storage removeItem error:', error);
  }
};

/**
 * Saves an object as JSON to persistent storage.
 * @param {string} key
 * @param {object} value
 */
export const saveObject = async (key, value) => {
  if (!canUseStorage()) return;
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    console.error('Storage saveObject error:', error);
  }
};

/**
 * Retrieves and parses a JSON object from persistent storage.
 * @param {string} key
 * @returns {object|null}
 */
export const getObject = async (key) => {
  if (!canUseStorage()) return null;
  try {
    const value = localStorage.getItem(key);
    return value ? JSON.parse(value) : null;
  } catch (error) {
    console.error('Storage getObject error:', error);
    return null;
  }
};

/**
 * Clears all app storage.
 */
export const clearStorage = async () => {
  if (!canUseStorage()) return;
  try {
    localStorage.clear();
  } catch (error) {
    console.error('Storage clearStorage error:', error);
  }
};
